import { useMemo, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { useRoles } from '../context/RolesContext'
import { OwnerFirmConfirm } from '../kit/OwnerConfirm'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { rollbackDemoWeb, type DemoWebRelease } from '../../../services/ownerDemoLaneService'
import { useDemoWebReleases } from './useDemoWebReleases'
import { DemoPhoneAppsSection } from './DemoPhoneAppsSection'
import { DemoReadyCard } from './DemoReadyCard'
import { DemoFreezeControl } from './DemoFreezeControl'
import './ownerDemoLane.css'

const shortSha = (sha: string | null): string => (sha ? sha.slice(0, 7) : '—')
const shortDate = (iso: string | null): string => (iso ? iso.slice(0, 16).replace('T', ' ') : '—')
const versionId = (name: string | null): string => (name ? name.split('/').pop() || name : '—')
const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error'

/**
 * Website section of the Demo lane (R35 DL3.b): the live demo release, the last 10 releases, Roll back.
 * Founder only; the server enforces the same (functions/src/demoWebReleases.ts).
 */
export function DemoWebsiteSection() {
  const { t } = useNls()
  const { data, loading, error, reload } = useDemoWebReleases(true)
  const [target, setTarget] = useState<DemoWebRelease | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const live = data?.releases.find((release) => release.isLive) ?? null

  const columns = useMemo<ColumnDef<DemoWebRelease>[]>(() => [
    { key: 'sha', header: t('NLSOwnerConsoleDemoLaneColCommit'), accessor: (r) => shortSha(r.gitSha), render: (r) => <code>{shortSha(r.gitSha)}</code> },
    { key: 'built', header: t('NLSOwnerConsoleDemoLaneColBuilt'), accessor: (r) => shortDate(r.builtAt) },
    { key: 'branch', header: t('NLSOwnerConsoleDemoLaneColBranch'), accessor: (r) => r.branch ?? '—' },
    { key: 'version', header: t('NLSOwnerConsoleDemoLaneColVersion'), accessor: (r) => versionId(r.hostingVersionName) },
    {
      key: 'status',
      header: t('NLSOwnerConsoleDemoLaneColStatus'),
      render: (r) => (r.isLive
        ? <span className="ownerDemoLaneBadge">{t('NLSOwnerConsoleDemoLaneStatusLive')}</span>
        : (
          <button
            type="button"
            className="ownerModalBtn ownerModalBtn--default"
            disabled={!r.hostingVersionName || busy}
            onClick={() => { setActionError(null); setDone(null); setTarget(r) }}
            aria-label={`${t('NLSOwnerConsoleDemoLaneRollBack')} ${shortSha(r.gitSha)}`}
          >
            {t('NLSOwnerConsoleDemoLaneRollBack')}
          </button>
        )),
    },
  ], [t, busy])

  const confirmRollback = async (reason: string) => {
    if (!target?.hostingVersionName) return
    setBusy(true)
    setActionError(null)
    try {
      await rollbackDemoWeb(target.hostingVersionName, reason)
      setDone(t('NLSOwnerConsoleDemoLaneRollBackDone', { sha: shortSha(target.gitSha) }))
      setTarget(null)
      await reload()
    } catch (err) {
      setActionError(messageOf(err))
      setTarget(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="ownerDemoLaneSection" aria-label={t('NLSOwnerConsoleDemoLaneWebsiteTitle')}>
      <div className="ownerPanelHeader">
        <div>
          <h3>{t('NLSOwnerConsoleDemoLaneWebsiteTitle')}</h3>
          <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneWebsiteHint')}</p>
        </div>
        <button type="button" className="ownerModalBtn ownerModalBtn--default" onClick={() => void reload()} disabled={loading || busy}>
          {t('NLSOwnerConsoleDemoLaneRefresh')}
        </button>
      </div>

      {loading && !data ? <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneLoading')}</p> : null}
      {error ? (
        <div role="alert">
          <strong>{t('NLSOwnerConsoleDemoLaneLoadFailed')}</strong>
          <p className="ownerDemoLaneError">{error}</p>
        </div>
      ) : null}
      {done ? <p className="ownerDemoLaneNote" role="status">{done}</p> : null}
      {actionError ? (
        <div role="alert">
          <strong>{t('NLSOwnerConsoleDemoLaneRollBackFailed')}</strong>
          <p className="ownerDemoLaneError">{actionError}</p>
        </div>
      ) : null}

      {data ? (
        <>
          <div className="ownerDemoLaneLive" aria-label={t('NLSOwnerConsoleDemoLaneWebsiteLive')}>
            <strong>{t('NLSOwnerConsoleDemoLaneWebsiteLive')}</strong>
            {live ? (
              <>
                <code>{shortSha(live.gitSha)}</code>
                <span>{shortDate(live.builtAt)}</span>
                <span>{versionId(live.hostingVersionName)}</span>
              </>
            ) : (
              <span>{t('NLSOwnerConsoleDemoLaneWebsiteLiveUnknown')}</span>
            )}
          </div>
          {data.liveError ? (
            <div role="alert">
              <strong>{t('NLSOwnerConsoleDemoLaneLiveError')}</strong>
              <p className="ownerDemoLaneError">{data.liveError}</p>
            </div>
          ) : null}
          <strong>{t('NLSOwnerConsoleDemoLaneWebsiteRecent')}</strong>
          <OwnerTable<DemoWebRelease>
            data={data.releases}
            columns={columns}
            viewKey="demo-lane-web"
            getRowId={(r) => r.releaseId}
            emptyState={<p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneWebsiteEmpty')}</p>}
          />
        </>
      ) : null}

      <OwnerFirmConfirm
        isOpen={target !== null}
        title={t('NLSOwnerConsoleDemoLaneRollBackTitle')}
        description={target ? t('NLSOwnerConsoleDemoLaneRollBackDescription', { sha: shortSha(target.gitSha), built: shortDate(target.builtAt) }) : undefined}
        confirmLabel={t('NLSOwnerConsoleDemoLaneRollBackConfirm')}
        busy={busy}
        onClose={() => setTarget(null)}
        onConfirm={confirmRollback}
      />
    </section>
  )
}

/** Demo lane view (route `config-demo-lane`). Sections are independent components so later steps plug in. */
export function OwnerDemoLanePanel() {
  const { t } = useNls()
  const { role } = useRoles()
  // Bumped by the freeze control so the readiness card re-checks (its Freeze check) after a change.
  const [readinessToken, setReadinessToken] = useState(0)

  if (role !== 'founder') {
    return (
      <section className="ownerPanel ownerDemoLane" aria-label={t('NLSOwnerConsoleDemoLaneTitle')}>
        <h2>{t('NLSOwnerConsoleDemoLaneTitle')}</h2>
        <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneFounderOnly')}</p>
      </section>
    )
  }

  return (
    <section className="ownerPanel ownerDemoLane" aria-label={t('NLSOwnerConsoleDemoLaneTitle')}>
      <div className="ownerPanelHeader">
        <div>
          <h2>{t('NLSOwnerConsoleDemoLaneTitle')}</h2>
          <p>{t('NLSOwnerConsoleDemoLaneSubtitle')}</p>
        </div>
      </div>
      <DemoReadyCard refreshToken={readinessToken} />
      <DemoFreezeControl onChanged={() => setReadinessToken((value) => value + 1)} />
      <DemoWebsiteSection />
      <DemoPhoneAppsSection />
    </section>
  )
}
